import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import '../styles/TwitterTools.css';

const TwitchPicker = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const [channelName, setChannelName] = useState('');
    const [keyword, setKeyword] = useState('!join');
    const [numberOfWinners, setNumberOfWinners] = useState(1);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    // Auto-collection states
    const [isMonitoring, setIsMonitoring] = useState(false);
    const [monitoringStatus, setMonitoringStatus] = useState(null);
    const [twitchLinked, setTwitchLinked] = useState(false);
    const [checkingLink, setCheckingLink] = useState(true);
    const [monitoringError, setMonitoringError] = useState('');
    const [pollingInterval, setPollingInterval] = useState(null);

    // Manual entry management
    const [chatEntries, setChatEntries] = useState([]);
    const [manualUsername, setManualUsername] = useState('');
    const [manualMessage, setManualMessage] = useState('');

    // Entry mode toggle
    const [collectionMode, setCollectionMode] = useState('auto'); // 'auto' or 'manual'
    const [showEntriesList, setShowEntriesList] = useState(false); // Toggle entries dropdown

    // Verification states
    const [verificationId, setVerificationId] = useState('');
    const [verificationResult, setVerificationResult] = useState(null);
    const [verifyingRoll, setVerifyingRoll] = useState(false);
    const [verificationError, setVerificationError] = useState('');

    // Reroll states
    const [showRerollModal, setShowRerollModal] = useState(false);
    const [rerollReason, setRerollReason] = useState('');
    const [rerolling, setRerolling] = useState(false);

    // History states
    const [giveawayHistory, setGiveawayHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [recentGiveaways, setRecentGiveaways] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [showRecentGiveaways, setShowRecentGiveaways] = useState(false);

    // Advanced Filter States
    const [advancedFiltersEnabled, setAdvancedFiltersEnabled] = useState(false);
    const [minAccountAge, setMinAccountAge] = useState(0); // Days
    const [minFollowDuration, setMinFollowDuration] = useState(0); // Days
    const [subscriberMultiplier, setSubscriberMultiplier] = useState(1); // 1-10x
    const [vipModMultiplier, setVipModMultiplier] = useState(1); // 1-10x
    const [bonusEntries, setBonusEntries] = useState([]); // Array of {username, entries}
    const [blacklist, setBlacklist] = useState(''); // Comma-separated usernames
    const [whitelist, setWhitelist] = useState(''); // Comma-separated usernames
    const [whitelistEnabled, setWhitelistEnabled] = useState(false);
    const [excludePreviousWinners, setExcludePreviousWinners] = useState(false);
    const [previousWinnersDays, setPreviousWinnersDays] = useState(30);
    const [minChatActivity, setMinChatActivity] = useState(0); // Minimum messages
    const [minEntryTime, setMinEntryTime] = useState('');
    const [maxEntryTime, setMaxEntryTime] = useState('');
    const [requireFollower, setRequireFollower] = useState(false);
    const [requireSubscriber, setRequireSubscriber] = useState(false);
    
    // Bonus entries management
    const [bonusUsername, setBonusUsername] = useState('');
    const [bonusAmount, setBonusAmount] = useState(1);

    // Check Twitch account link status on mount
    useEffect(() => {
        checkTwitchLinkStatus();
    }, [isAuthenticated]);

    // Polling for entries when monitoring
    useEffect(() => {
        if (isMonitoring && channelName) {
            const interval = setInterval(() => {
                fetchCollectedEntries();
            }, 2000); // Poll every 2 seconds
            
            setPollingInterval(interval);
            
            return () => clearInterval(interval);
        }
    }, [isMonitoring, channelName]);

    const checkTwitchLinkStatus = async () => {
        if (!isAuthenticated) {
            setCheckingLink(false);
            return;
        }

        try {
            const response = await axios.get('/api/twitch/account/status');
            setTwitchLinked(response.data.linked);
        } catch (err) {
            console.error('Failed to check Twitch link status:', err);
        } finally {
            setCheckingLink(false);
        }
    };

    const handleLinkTwitch = async () => {
        try {
            const response = await axios.get('/api/twitch/oauth/initiate');
            if (response.data.success) {
                window.location.href = response.data.authUrl;
            }
        } catch (err) {
            alert('Failed to initiate Twitch authorization');
        }
    };

    const handleStartMonitoring = async () => {
        if (!channelName.trim()) {
            setMonitoringError('Please enter a channel name');
            return;
        }

        if (!keyword.trim()) {
            setMonitoringError('Please enter a keyword');
            return;
        }

        setMonitoringError('');
        setLoading(true);

        try {
            const response = await axios.post('/api/twitch/start-monitoring', {
                channelName: channelName.trim(),
                keyword: keyword.trim()
            });

            if (response.data.success) {
                setIsMonitoring(true);
                setMonitoringStatus(response.data);
                setChatEntries([]); // Clear previous entries
            }
        } catch (err) {
            console.error(err);
            setMonitoringError(err.response?.data?.message || 'Failed to start monitoring');
        } finally {
            setLoading(false);
        }
    };

    const handleStopMonitoring = async () => {
        if (!channelName.trim()) return;

        setLoading(true);

        try {
            const response = await axios.post('/api/twitch/stop-monitoring', {
                channelName: channelName.trim()
            });

            if (response.data.success) {
                setIsMonitoring(false);
                setMonitoringStatus(null);
                
                // Clear polling interval
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    setPollingInterval(null);
                }
            }
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to stop monitoring');
        } finally {
            setLoading(false);
        }
    };

    const fetchCollectedEntries = async () => {
        if (!channelName.trim()) return;

        try {
            const response = await axios.get(`/api/twitch/collected-entries?channelName=${channelName.trim()}`);
            
            if (response.data.success) {
                setChatEntries(response.data.entries || []);
            }
        } catch (err) {
            // Silently fail - monitoring might have stopped
            console.error('Failed to fetch entries:', err);
        }
    };

    const handleClearCollectedEntries = async () => {
        if (!window.confirm('Are you sure you want to clear all collected entries?')) {
            return;
        }

        try {
            const response = await axios.post('/api/twitch/clear-entries', {
                channelName: channelName.trim()
            });

            if (response.data.success) {
                setChatEntries([]);
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to clear entries');
        }
    };

    // Add manual entry
    const handleAddEntry = () => {
        if (!manualUsername.trim() || !manualMessage.trim()) {
            alert('Please enter both username and message');
            return;
        }

        const newEntry = {
            username: manualUsername.trim().toLowerCase().replace(/^@/, ''),
            displayName: manualUsername.trim().replace(/^@/, ''),
            userId: Date.now().toString(),
            message: manualMessage.trim(),
            entryTime: new Date()
        };

        setChatEntries([...chatEntries, newEntry]);
        setManualUsername('');
        setManualMessage('');
    };

    // Remove entry
    const handleRemoveEntry = (index) => {
        setChatEntries(chatEntries.filter((_, i) => i !== index));
    };

    // Clear all entries
    const handleClearEntries = () => {
        if (window.confirm('Are you sure you want to clear all entries?')) {
            setChatEntries([]);
        }
    };

    // Bonus entry management
    const handleAddBonusEntry = () => {
        if (!bonusUsername.trim()) {
            alert('Please enter a username');
            return;
        }
        const username = bonusUsername.trim().toLowerCase();
        const existing = bonusEntries.find(b => b.username === username);
        if (existing) {
            setBonusEntries(bonusEntries.map(b => 
                b.username === username ? { ...b, entries: bonusAmount } : b
            ));
        } else {
            setBonusEntries([...bonusEntries, { username, entries: bonusAmount }]);
        }
        setBonusUsername('');
        setBonusAmount(1);
    };

    const handleRemoveBonusEntry = (username) => {
        setBonusEntries(bonusEntries.filter(b => b.username !== username));
    };

    const handlePickWinners = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);
        setLoading(true);

        try {
            const advancedFilters = advancedFiltersEnabled ? {
                minAccountAge,
                minFollowDuration,
                subscriberMultiplier,
                vipModMultiplier,
                bonusEntries,
                blacklist: blacklist.split(',').map(u => u.trim().toLowerCase()).filter(Boolean),
                whitelist: whitelistEnabled ? whitelist.split(',').map(u => u.trim().toLowerCase()).filter(Boolean) : [],
                whitelistEnabled,
                excludePreviousWinners,
                previousWinnersDays,
                minChatActivity,
                minEntryTime,
                maxEntryTime,
                requireFollower,
                requireSubscriber
            } : null;

            const response = await axios.post('/api/twitch/pick-chat-winner', {
                channelName,
                keyword,
                entries: chatEntries,
                numberOfWinners: parseInt(numberOfWinners),
                advancedFilters
            });

            if (response.data.success) {
                setResult(response.data.data);
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to pick winners');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };

    // Verification
    const handleVerifyGiveaway = async (e) => {
        e.preventDefault();
        setVerificationError('');
        setVerificationResult(null);
        setVerifyingRoll(true);

        try {
            const response = await axios.post('/api/twitch/verify', {
                verificationId: verificationId.trim()
            });

            if (response.data.success) {
                setVerificationResult(response.data.data);
                setVerificationId('');
            }
        } catch (err) {
            console.error(err);
            setVerificationError(err.response?.data?.message || 'Verification failed');
        } finally {
            setVerifyingRoll(false);
        }
    };

    // Reroll
    const handleReroll = async () => {
        if (!result?.verificationId) {
            alert('No giveaway to reroll');
            return;
        }

        if (!rerollReason.trim()) {
            alert('Please provide a reason for rerolling');
            return;
        }

        setRerolling(true);

        try {
            const response = await axios.post('/api/twitch/reroll', {
                verificationId: result.verificationId,
                rerollReason: rerollReason.trim()
            });

            if (response.data.success) {
                setResult(response.data.data);
                setShowRerollModal(false);
                setRerollReason('');
                alert('Giveaway rerolled successfully!');
            }
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to reroll giveaway');
        } finally {
            setRerolling(false);
        }
    };

    // Load giveaway history
    const loadGiveawayHistory = async () => {
        if (!isAuthenticated) return;

        setLoadingHistory(true);
        try {
            const response = await axios.get('/api/twitch/giveaway-history');
            if (response.data.success) {
                setGiveawayHistory(response.data.data);
                setShowHistory(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load giveaway history');
        } finally {
            setLoadingHistory(false);
        }
    };

    // Load recent giveaways
    const loadRecentGiveaways = async () => {
        setLoadingRecent(true);
        try {
            const response = await axios.get('/api/twitch/recent-giveaways');
            if (response.data.success) {
                setRecentGiveaways(response.data.data);
                setShowRecentGiveaways(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load recent giveaways');
        } finally {
            setLoadingRecent(false);
        }
    };

    useEffect(() => {
        loadRecentGiveaways();
    }, []);

    // Get unique participants count
    const uniqueParticipants = new Set(chatEntries.map(e => e.username)).size;

    return (
        <div className="twitter-tools-container">
            <div className="tools-header">
                <div className="badge-featured">Creator Suite</div>
                <h1>🎮 Twitch Chat Picker</h1>
                <p>Pick random winners from your Twitch chat using keywords</p>
            </div>

            <div className="tools-form-container">
                {/* Verification Section */}
                <div className="verification-section">
                    <h3>🔍 Verify Giveaway Roll</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        Enter a verification ID to check giveaway details and winners
                    </p>
                    <form onSubmit={handleVerifyGiveaway} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                            <input
                                type="text"
                                value={verificationId}
                                onChange={(e) => setVerificationId(e.target.value.toUpperCase())}
                                placeholder="Enter 12-character verification ID"
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: '8px',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={verifyingRoll}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #9147ff 0%, #6441a5 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            {verifyingRoll ? '🔍 Verifying...' : '🔍 Verify'}
                        </button>
                    </form>
                    {verificationError && (
                        <div className="error-message" style={{ marginTop: '1rem' }}>
                            <span className="error-icon">⚠️</span>
                            {verificationError}
                        </div>
                    )}
                </div>

                {/* Main Picker Form */}
                <form onSubmit={handlePickWinners} className="tools-form">
                    <div className="form-group">
                        <label htmlFor="channelName">
                            Twitch Channel Name *
                            <span className="label-hint">Your Twitch username</span>
                        </label>
                        <input
                            type="text"
                            id="channelName"
                            value={channelName}
                            onChange={(e) => setChannelName(e.target.value)}
                            placeholder="ninja"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="keyword">
                            Entry Keyword *
                            <span className="label-hint">Keyword viewers type to enter (e.g., !join, !enter, !giveaway)</span>
                        </label>
                        <input
                            type="text"
                            id="keyword"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder="!join"
                            required
                        />
                    </div>

                    {/* Collection Mode Toggle */}
                    <div className="form-group">
                        <label>Collection Mode</label>
                        <div className="twitch-mode-toggle">
                            <button
                                type="button"
                                onClick={() => setCollectionMode('auto')}
                                className={`twitch-mode-btn ${collectionMode === 'auto' ? 'active' : ''}`}
                            >
                                <span className="mode-icon">🤖</span>
                                <span className="mode-text">
                                    <strong>Auto-Collect</strong>
                                    <small>Live IRC Monitoring</small>
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setCollectionMode('manual')}
                                className={`twitch-mode-btn ${collectionMode === 'manual' ? 'active' : ''}`}
                            >
                                <span className="mode-icon">✋</span>
                                <span className="mode-text">
                                    <strong>Manual Entry</strong>
                                    <small>Add Participants Manually</small>
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Auto-Collection Section */}
                    {collectionMode === 'auto' && (
                        <div className="twitch-auto-section">
                            <div className="section-header">
                                <span className="header-icon">🤖</span>
                                <h3>Auto-Collect from Live Chat</h3>
                            </div>

                            {!twitchLinked ? (
                                <div className="twitch-link-card">
                                    <div className="link-card-icon">🔗</div>
                                    <h4>Connect Your Twitch Account</h4>
                                    <p>
                                        Link your Twitch account to automatically monitor chat and collect entries 
                                        when viewers type your keyword in real-time.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleLinkTwitch}
                                        disabled={checkingLink}
                                        className="twitch-link-btn"
                                    >
                                        {checkingLink ? '🔄 Checking...' : '🔗 Connect Twitch Account'}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="twitch-connected-badge">
                                        <span className="badge-icon">✅</span>
                                        <span className="badge-text">
                                            <strong>Twitch Connected!</strong>
                                            <small>Ready to monitor your chat</small>
                                        </span>
                                    </div>

                                    {monitoringError && (
                                        <div className="twitch-error-card">
                                            <span className="error-icon">⚠️</span>
                                            <span>{monitoringError}</span>
                                        </div>
                                    )}

                                    {!isMonitoring ? (
                                        <button
                                            type="button"
                                            onClick={handleStartMonitoring}
                                            disabled={loading || !channelName.trim() || !keyword.trim()}
                                            className="twitch-start-btn"
                                        >
                                            <span className="btn-icon">▶️</span>
                                            {loading ? 'Starting...' : 'Start Monitoring Chat'}
                                        </button>
                                    ) : (
                                        <>
                                            <div className="twitch-live-indicator">
                                                <div className="live-dot"></div>
                                                <div className="live-text">
                                                    <strong>LIVE</strong>
                                                    <span>Monitoring {channelName} for "{keyword}"</span>
                                                </div>
                                            </div>

                                            <div className="twitch-control-buttons">
                                                <button
                                                    type="button"
                                                    onClick={handleStopMonitoring}
                                                    disabled={loading}
                                                    className="twitch-stop-btn"
                                                >
                                                    {loading ? '⏸️ Stopping...' : '⏹️ Stop Monitoring'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleClearCollectedEntries}
                                                    className="twitch-clear-btn"
                                                >
                                                    🗑️ Clear Entries
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Manual Entry Section */}
                    {collectionMode === 'manual' && (
                    <div className="youtube-filters-section">
                        <div className="youtube-filters-header">
                            <div className="youtube-filters-header-icon">💬</div>
                            <h3>Collect Chat Entries</h3>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <div className="info-notice" style={{
                                padding: '1rem',
                                background: 'rgba(145, 71, 255, 0.1)',
                                border: '2px solid rgba(145, 71, 255, 0.3)',
                                borderRadius: '8px',
                                marginBottom: '1rem'
                            }}>
                                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                                    📝 <strong>How to collect entries:</strong> Manually add each viewer who types the keyword in your Twitch chat. 
                                    Copy their username and message, then add them below. Make sure they include the keyword "{keyword}" in their message.
                                </p>
                            </div>

                            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                                <input
                                    type="text"
                                    value={manualUsername}
                                    onChange={(e) => setManualUsername(e.target.value)}
                                    placeholder="Username (e.g., viewer123)"
                                    className="youtube-filter-text-input"
                                    style={{ flex: '1' }}
                                />
                                <input
                                    type="text"
                                    value={manualMessage}
                                    onChange={(e) => setManualMessage(e.target.value)}
                                    placeholder={`Message (must include ${keyword})`}
                                    className="youtube-filter-text-input"
                                    style={{ flex: '2' }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddEntry}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: 'linear-gradient(135deg, #9147ff 0%, #6441a5 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    ➕ Add Entry
                                </button>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1rem'
                            }}>
                                <p style={{ color: 'var(--text-primary)', margin: 0, fontWeight: '600' }}>
                                    Total Entries: {chatEntries.length} | Unique Participants: {uniqueParticipants}
                                </p>
                                {chatEntries.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleClearEntries}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: 'rgba(239, 68, 68, 0.2)',
                                            color: '#ef4444',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        🗑️ Clear All
                                    </button>
                                )}
                            </div>

                            {chatEntries.length > 0 && (
                                <div style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '8px',
                                    padding: '0.5rem'
                                }}>
                                    {chatEntries.map((entry, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.75rem',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ margin: 0, fontWeight: '600', color: '#9147ff' }}>
                                                    {entry.displayName}
                                                </p>
                                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                    {entry.message}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveEntry(index)}
                                                style={{
                                                    padding: '0.5rem',
                                                    background: 'transparent',
                                                    color: '#ef4444',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                    {/* Entry Stats - Show for both modes */}
                    {chatEntries.length > 0 && (
                        <div className="twitch-entries-section">
                            <div className="entries-stats-card">
                                <div className="stat-box">
                                    <div className="stat-icon">📊</div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Entries</div>
                                        <div className="stat-value">{chatEntries.length}</div>
                                    </div>
                                </div>
                                <div className="stat-box">
                                    <div className="stat-icon">👥</div>
                                    <div className="stat-content">
                                        <div className="stat-label">Unique Participants</div>
                                        <div className="stat-value">{uniqueParticipants}</div>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowEntriesList(!showEntriesList)}
                                className="twitch-entries-toggle"
                            >
                                <span className="toggle-icon">{showEntriesList ? '▼' : '▶'}</span>
                                <span className="toggle-text">
                                    {showEntriesList ? 'Hide' : 'Show'} Entries List
                                </span>
                                <span className="entries-badge">{chatEntries.length}</span>
                            </button>

                            {showEntriesList && (
                                <div className="twitch-entries-list">
                                    <div className="entries-list-header">
                                        <span>Participant</span>
                                        <span>Message</span>
                                    </div>
                                    <div className="entries-list-body">
                                        {chatEntries.map((entry, index) => (
                                            <div key={index} className="entry-item">
                                                <div className="entry-user">
                                                    <span className="entry-number">#{index + 1}</span>
                                                    <span className="entry-displayname">{entry.displayName}</span>
                                                </div>
                                                <div className="entry-message">{entry.message}</div>
                                                {collectionMode === 'manual' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveEntry(index)}
                                                        className="entry-remove-btn"
                                                        title="Remove entry"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Advanced Filters Section */}
                    {chatEntries.length > 0 && (
                        <div className="youtube-filters-section">
                            <div className="youtube-filters-header">
                                <div className="youtube-filters-header-icon">⚙️</div>
                                <h3>Advanced Filters & Settings</h3>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.05))',
                                border: '2px solid rgba(99, 102, 241, 0.3)',
                                borderRadius: '12px',
                                padding: '1rem 1.25rem',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                    <span style={{ fontSize: '1.5rem', marginTop: '0.15rem' }}>💡</span>
                                    <div>
                                        <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '0.5rem' }}>
                                            Take Full Control of Your Giveaways!
                                        </strong>
                                        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
                                            Use advanced filters to: exclude previous winners, give bonus entries to loyal viewers, 
                                            require followers/subscribers, block specific users, and much more. Mix and match filters 
                                            to create the perfect giveaway rules for your community!
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="filter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={advancedFiltersEnabled}
                                        onChange={(e) => setAdvancedFiltersEnabled(e.target.checked)}
                                    />
                                    <span>Enable Advanced Filtering</span>
                                </label>
                                <span className="filters-hint" style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    Apply sophisticated filters to control who can win and add bonus entries
                                </span>
                            </div>

                            {advancedFiltersEnabled && (
                                <div className="youtube-filter-grid">
                                    {/* Account Age Filter */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={minAccountAge > 0}
                                                onChange={(e) => setMinAccountAge(e.target.checked ? 30 : 0)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                🗓️ Minimum Account Age
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Exclude accounts newer than specified days (prevents fake/alt accounts)
                                        </p>
                                        {minAccountAge > 0 && (
                                            <input
                                                type="number"
                                                value={minAccountAge}
                                                onChange={(e) => setMinAccountAge(Math.max(0, parseInt(e.target.value) || 0))}
                                                placeholder="Days (e.g., 30)"
                                                className="youtube-filter-text-input"
                                                min="0"
                                            />
                                        )}
                                    </div>

                                    {/* Follow Duration Filter */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={requireFollower}
                                                onChange={(e) => setRequireFollower(e.target.checked)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                💜 Followers Only
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Only allow followers to win. Optionally set minimum follow duration.
                                        </p>
                                        {requireFollower && (
                                            <input
                                                type="number"
                                                value={minFollowDuration}
                                                onChange={(e) => setMinFollowDuration(Math.max(0, parseInt(e.target.value) || 0))}
                                                placeholder="Min follow days (0 = any)"
                                                className="youtube-filter-text-input"
                                                min="0"
                                            />
                                        )}
                                    </div>

                                    {/* Subscriber Requirement */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={requireSubscriber}
                                                onChange={(e) => setRequireSubscriber(e.target.checked)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                ⭐ Subscribers Only
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Only allow subscribers to win (includes gifted subs)
                                        </p>
                                    </div>

                                    {/* Subscriber Multiplier */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={subscriberMultiplier > 1}
                                                onChange={(e) => setSubscriberMultiplier(e.target.checked ? 2 : 1)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                ⭐ Subscriber Bonus Entries
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Give subscribers extra entries (2-10x multiplier)
                                        </p>
                                        {subscriberMultiplier > 1 && (
                                            <input
                                                type="number"
                                                value={subscriberMultiplier}
                                                onChange={(e) => setSubscriberMultiplier(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                                                placeholder="Multiplier (2-10)"
                                                className="youtube-filter-text-input"
                                                min="1"
                                                max="10"
                                            />
                                        )}
                                    </div>

                                    {/* VIP/Mod Multiplier */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={vipModMultiplier > 1}
                                                onChange={(e) => setVipModMultiplier(e.target.checked ? 2 : 1)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                👑 VIP/Mod Bonus Entries
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Give VIPs and Moderators extra entries (2-10x multiplier)
                                        </p>
                                        {vipModMultiplier > 1 && (
                                            <input
                                                type="number"
                                                value={vipModMultiplier}
                                                onChange={(e) => setVipModMultiplier(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                                                placeholder="Multiplier (2-10)"
                                                className="youtube-filter-text-input"
                                                min="1"
                                                max="10"
                                            />
                                        )}
                                    </div>

                                    {/* Minimum Chat Activity */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={minChatActivity > 0}
                                                onChange={(e) => setMinChatActivity(e.target.checked ? 10 : 0)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                💬 Minimum Chat Activity
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Require minimum number of messages sent in channel history
                                        </p>
                                        {minChatActivity > 0 && (
                                            <input
                                                type="number"
                                                value={minChatActivity}
                                                onChange={(e) => setMinChatActivity(Math.max(0, parseInt(e.target.value) || 0))}
                                                placeholder="Min messages (e.g., 10)"
                                                className="youtube-filter-text-input"
                                                min="0"
                                            />
                                        )}
                                    </div>

                                    {/* Previous Winner Exclusion */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={excludePreviousWinners}
                                                onChange={(e) => setExcludePreviousWinners(e.target.checked)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                🚫 Exclude Previous Winners
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Exclude users who won in your previous giveaways
                                        </p>
                                        {excludePreviousWinners && (
                                            <input
                                                type="number"
                                                value={previousWinnersDays}
                                                onChange={(e) => setPreviousWinnersDays(Math.max(1, parseInt(e.target.value) || 30))}
                                                placeholder="Exclude winners from last X days"
                                                className="youtube-filter-text-input"
                                                min="1"
                                            />
                                        )}
                                    </div>

                                    {/* Time Range Filter */}
                                    <div className="youtube-filter-card">
                                        <div className="youtube-filter-title" style={{ marginBottom: '0.5rem' }}>
                                            🕐 Entry Time Range
                                        </div>
                                        <p className="youtube-filter-description">
                                            Only count entries within specific time window
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                                            <input
                                                type="datetime-local"
                                                value={minEntryTime}
                                                onChange={(e) => setMinEntryTime(e.target.value)}
                                                placeholder="Start time"
                                                className="youtube-filter-datetime-input"
                                            />
                                            <input
                                                type="datetime-local"
                                                value={maxEntryTime}
                                                onChange={(e) => setMaxEntryTime(e.target.value)}
                                                placeholder="End time"
                                                className="youtube-filter-datetime-input"
                                            />
                                        </div>
                                    </div>

                                    {/* Blacklist */}
                                    <div className="youtube-filter-card">
                                        <div className="youtube-filter-title" style={{ marginBottom: '0.5rem' }}>
                                            🚫 Blacklist Users
                                        </div>
                                        <p className="youtube-filter-description">
                                            Exclude specific users from winning (comma-separated)
                                        </p>
                                        <input
                                            type="text"
                                            value={blacklist}
                                            onChange={(e) => setBlacklist(e.target.value)}
                                            placeholder="user1, user2, user3"
                                            className="youtube-filter-text-input"
                                        />
                                    </div>

                                    {/* Whitelist */}
                                    <div className="youtube-filter-card">
                                        <label className="youtube-filter-label">
                                            <input
                                                type="checkbox"
                                                checked={whitelistEnabled}
                                                onChange={(e) => setWhitelistEnabled(e.target.checked)}
                                                className="youtube-filter-input"
                                            />
                                            <div className="youtube-filter-title">
                                                ✅ Whitelist Only Mode
                                            </div>
                                        </label>
                                        <p className="youtube-filter-description">
                                            Only allow specific users to win (comma-separated)
                                        </p>
                                        {whitelistEnabled && (
                                            <input
                                                type="text"
                                                value={whitelist}
                                                onChange={(e) => setWhitelist(e.target.value)}
                                                placeholder="user1, user2, user3"
                                                className="youtube-filter-text-input"
                                            />
                                        )}
                                    </div>

                                    {/* Bonus Entries */}
                                    <div className="youtube-filter-card" style={{ gridColumn: '1 / -1' }}>
                                        <div className="youtube-filter-title" style={{ marginBottom: '0.5rem' }}>
                                            🎁 Custom Bonus Entries
                                        </div>
                                        <p className="youtube-filter-description">
                                            Give specific users extra entries (e.g., reward loyal viewers)
                                        </p>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                            <input
                                                type="text"
                                                value={bonusUsername}
                                                onChange={(e) => setBonusUsername(e.target.value)}
                                                placeholder="Username"
                                                className="youtube-filter-text-input"
                                                style={{ flex: 2 }}
                                            />
                                            <input
                                                type="number"
                                                value={bonusAmount}
                                                onChange={(e) => setBonusAmount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                                                placeholder="Bonus"
                                                className="youtube-filter-text-input"
                                                style={{ flex: 1 }}
                                                min="1"
                                                max="100"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddBonusEntry}
                                                style={{
                                                    padding: '0.75rem 1rem',
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                ➕ Add
                                            </button>
                                        </div>
                                        {bonusEntries.length > 0 && (
                                            <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {bonusEntries.map((bonus, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.5rem',
                                                            padding: '0.5rem 0.75rem',
                                                            background: 'rgba(16, 185, 129, 0.2)',
                                                            border: '1px solid rgba(16, 185, 129, 0.4)',
                                                            borderRadius: '20px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: '600' }}>{bonus.username}</span>
                                                        <span style={{ color: '#10b981' }}>+{bonus.entries}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveBonusEntry(bonus.username)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: '#ef4444',
                                                                cursor: 'pointer',
                                                                padding: '0',
                                                                fontSize: '1rem'
                                                            }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="numberOfWinners">
                            Number of Winners *
                            <span className="label-hint">1-50 winners</span>
                        </label>
                        <input
                            type="number"
                            id="numberOfWinners"
                            value={numberOfWinners}
                            onChange={(e) => setNumberOfWinners(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                            min="1"
                            max="50"
                            required
                        />
                    </div>

                    <button 
                        type="submit" 
                        className="tools-btn" 
                        disabled={loading || !isAuthenticated || chatEntries.length === 0}
                    >
                        {loading ? 'Picking Winners...' : `🎯 Pick ${numberOfWinners} Winner${numberOfWinners > 1 ? 's' : ''}`}
                    </button>
                </form>

                <div className="info-notice" style={{
                    marginTop: '1.5rem',
                    padding: '1rem 1.5rem',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>✨</span>
                    <small style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        <strong>100% FREE!</strong> This tool is completely free to use - no credits required.
                    </small>
                </div>

                {!isAuthenticated && (
                    <div className="info-notice" style={{
                        marginTop: '1rem',
                        padding: '1rem 1.5rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '12px'
                    }}>
                        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                            🔒 Please <a href="/login" style={{ color: 'var(--accent-color)' }}>log in</a> to use this tool.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="error-message" style={{ marginTop: '1rem' }}>
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}

                {result && (
                    <div className="twitch-results-section">
                        <div className="results-header-banner">
                            <div className="results-icon">🎉</div>
                            <h2>Winners Selected!</h2>
                        </div>

                        <div className="results-main-card">
                            <div className="results-channel-info">
                                <div className="channel-badge">
                                    <span className="badge-icon">📺</span>
                                    <span className="badge-text">{result.channelName}</span>
                                </div>
                                <div className="keyword-badge">
                                    <span className="badge-icon">🎯</span>
                                    <span className="badge-label">Keyword:</span>
                                    <span className="badge-text">{result.keyword}</span>
                                </div>
                            </div>

                            <div className="results-stats-grid">
                                <div className="results-stat-card">
                                    <div className="stat-icon-circle">📊</div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Entries</div>
                                        <div className="stat-value">{result.totalEntries}</div>
                                    </div>
                                </div>
                                
                                <div className="results-stat-card">
                                    <div className="stat-icon-circle">👥</div>
                                    <div className="stat-content">
                                        <div className="stat-label">Unique Participants</div>
                                        <div className="stat-value">{result.uniqueParticipants}</div>
                                    </div>
                                </div>
                                
                                <div className="results-stat-card">
                                    <div className="stat-icon-circle">🏆</div>
                                    <div className="stat-content">
                                        <div className="stat-label">Winners</div>
                                        <div className="stat-value">{result.numberOfWinners}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Filter Summary */}
                            {result.advancedFilters && result.filterSummary && (
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
                                    border: '2px solid rgba(99, 102, 241, 0.3)',
                                    borderRadius: '16px',
                                    padding: '1.5rem',
                                    marginTop: '1.5rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <span style={{ fontSize: '1.5rem' }}>⚙️</span>
                                        <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.2rem', fontWeight: '700' }}>
                                            Advanced Filters Applied
                                        </h4>
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        <div style={{
                                            padding: '0.75rem',
                                            background: 'var(--bg-secondary)',
                                            borderRadius: '10px',
                                            border: '1px solid var(--border-color)'
                                        }}>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                ORIGINAL ENTRIES
                                            </div>
                                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                                                {result.filterSummary.originalCount}
                                            </div>
                                        </div>
                                        
                                        {result.filterSummary.afterKeywordFilter !== result.filterSummary.originalCount && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid var(--border-color)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    AFTER KEYWORD
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
                                                    {result.filterSummary.afterKeywordFilter}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.advancedFilters.blacklist && result.advancedFilters.blacklist.length > 0 && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(239, 68, 68, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    🚫 BLACKLISTED
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444' }}>
                                                    -{result.filterSummary.afterKeywordFilter - (result.filterSummary.afterBlacklist || result.filterSummary.afterKeywordFilter)}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.advancedFilters.whitelistEnabled && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(16, 185, 129, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    ✅ WHITELIST ONLY
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                                                    {result.filterSummary.afterWhitelist || 0}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.advancedFilters.excludePreviousWinners && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(245, 158, 11, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    🚫 PREV WINNERS
                                                </div>
                                                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#f59e0b' }}>
                                                    Last {result.advancedFilters.previousWinnersDays} days
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.filterSummary.bonusEntriesApplied > 0 && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(16, 185, 129, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    🎁 BONUS ENTRIES
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                                                    +{result.filterSummary.bonusEntriesApplied}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.filterSummary.subscriberBonusApplied > 0 && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(139, 92, 246, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    ⭐ SUB BONUS ({result.advancedFilters.subscriberMultiplier}x)
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
                                                    {result.filterSummary.subscriberBonusApplied} users
                                                </div>
                                            </div>
                                        )}
                                        
                                        {result.filterSummary.vipModBonusApplied > 0 && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(251, 191, 36, 0.3)'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                                                    👑 VIP/MOD BONUS ({result.advancedFilters.vipModMultiplier}x)
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fbbf24' }}>
                                                    {result.filterSummary.vipModBonusApplied} users
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div style={{
                                            padding: '0.75rem',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.1))',
                                            borderRadius: '10px',
                                            border: '2px solid rgba(16, 185, 129, 0.5)'
                                        }}>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '600' }}>
                                                ✅ FINAL COUNT
                                            </div>
                                            <div style={{ fontSize: '1.75rem', fontWeight: '900', color: '#10b981' }}>
                                                {result.filterSummary.finalCount}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="results-verification-card">
                                <div className="verification-card-header">
                                    <span className="verification-label">🔐 Verification ID</span>
                                </div>
                                <div 
                                    className="verification-id-code" 
                                    onClick={() => copyToClipboard(result.verificationId)}
                                >
                                    <span className="id-code-text">{result.verificationId}</span>
                                    <span className="copy-action">
                                        <span className="copy-icon">📋</span>
                                        <span className="copy-label">Click to copy</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="results-winners-section">
                            <div className="results-winners-header">
                                <div className="header-left">
                                    <span className="header-trophy">🏆</span>
                                    <h3>Winners ({result.numberOfWinners})</h3>
                                </div>
                                <button
                                    className="reroll-button"
                                    onClick={() => setShowRerollModal(true)}
                                >
                                    <span className="button-icon">🔄</span>
                                    <span className="button-text">Reroll</span>
                                </button>
                            </div>

                            <div className="results-winners-grid">
                                {result.winners.map((winner, index) => (
                                    <div key={index} className="results-winner-card">
                                        <div className="winner-rank-badge">
                                            <span className="rank-number">#{index + 1}</span>
                                        </div>
                                        <div className="winner-card-content">
                                            <div className="winner-name-display">{winner.displayName}</div>
                                            <div className="winner-username-display">@{winner.username}</div>
                                            <div className="winner-message-display">
                                                <span className="message-icon">💬</span>
                                                <span className="message-text">{winner.message}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Verification Result */}
                {verificationResult && (
                    <div className="twitch-verification-section">
                        <div className="verification-header-banner">
                            <div className="verification-icon">✅</div>
                            <h2>Verification Result</h2>
                        </div>

                        <div className="verification-main-card">
                            <div className="verification-id-display">
                                <span className="id-label">Verification ID</span>
                                <span className="id-code">#{verificationResult.verificationId}</span>
                                {verificationResult.isReroll && (
                                    <span className="reroll-badge-tag">🔄 REROLLED</span>
                                )}
                            </div>

                            <div className="verification-info-grid">
                                <div className="info-cell">
                                    <span className="cell-icon">📺</span>
                                    <div className="cell-content">
                                        <span className="cell-label">Channel</span>
                                        <span className="cell-value">{verificationResult.channelName}</span>
                                    </div>
                                </div>
                                
                                <div className="info-cell">
                                    <span className="cell-icon">🎯</span>
                                    <div className="cell-content">
                                        <span className="cell-label">Keyword</span>
                                        <span className="cell-value">{verificationResult.keyword}</span>
                                    </div>
                                </div>
                                
                                <div className="info-cell">
                                    <span className="cell-icon">👥</span>
                                    <div className="cell-content">
                                        <span className="cell-label">Participants</span>
                                        <span className="cell-value">{verificationResult.uniqueParticipants}</span>
                                    </div>
                                </div>
                                
                                <div className="info-cell">
                                    <span className="cell-icon">👤</span>
                                    <div className="cell-content">
                                        <span className="cell-label">Rolled by</span>
                                        <span className="cell-value">@{verificationResult.rolledBy}</span>
                                    </div>
                                </div>
                                
                                <div className="info-cell full-width">
                                    <span className="cell-icon">📅</span>
                                    <div className="cell-content">
                                        <span className="cell-label">Rolled</span>
                                        <span className="cell-value">{new Date(verificationResult.rolledAt).toLocaleString()}</span>
                                    </div>
                                </div>
                                
                                {verificationResult.rerollReason && (
                                    <div className="info-cell full-width reroll-reason-cell">
                                        <span className="cell-icon">🔄</span>
                                        <div className="cell-content">
                                            <span className="cell-label">Reroll Reason</span>
                                            <span className="cell-value">{verificationResult.rerollReason}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="verification-winners-section">
                            <div className="winners-section-header">
                                <span className="header-icon">🏆</span>
                                <h3>Winners ({verificationResult.numberOfWinners})</h3>
                            </div>
                            
                            <div className="verification-winners-grid">
                                {verificationResult.winners.map((winner, index) => (
                                    <div key={winner.userId} className="verification-winner-card">
                                        <div className="winner-rank">#{index + 1}</div>
                                        <div className="winner-details">
                                            <div className="winner-name">{winner.displayName}</div>
                                            <div className="winner-handle">@{winner.username}</div>
                                            <div className="winner-entry">{winner.message}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {verificationResult.rollHistory && verificationResult.rollHistory.length > 1 && (
                            <div className="verification-history-section">
                                <div className="history-section-header">
                                    <span className="header-icon">📜</span>
                                    <h3>Roll History</h3>
                                </div>
                                
                                <div className="roll-history-timeline">
                                    {verificationResult.rollHistory.map((roll, index) => (
                                        <div key={roll.verificationId} className="timeline-item">
                                            <div className="timeline-marker">
                                                <div className="marker-dot"></div>
                                                {index < verificationResult.rollHistory.length - 1 && (
                                                    <div className="marker-line"></div>
                                                )}
                                            </div>
                                            <div className="timeline-content">
                                                <div className="timeline-header">
                                                    <span className="roll-badge">Roll #{roll.rollNumber}</span>
                                                    <span className="roll-date">{new Date(roll.rolledAt).toLocaleString()}</span>
                                                </div>
                                                <div className="timeline-info">
                                                    <span className="info-item">
                                                        <span className="info-icon">🏆</span>
                                                        {roll.numberOfWinners} {roll.numberOfWinners === 1 ? 'Winner' : 'Winners'}
                                                    </span>
                                                    <span className="info-item">
                                                        <span className="info-icon">👤</span>
                                                        {roll.rolledBy}
                                                    </span>
                                                </div>
                                                {roll.rerollReason && (
                                                    <div className="timeline-reroll-reason">
                                                        <span className="reason-icon">🔄</span>
                                                        <span className="reason-text">{roll.rerollReason}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Reroll Modal */}
                {showRerollModal && (
                    <div className="modal-overlay" onClick={() => setShowRerollModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h3>🔄 Reroll Giveaway</h3>
                            <p>Please provide a reason for rerolling this giveaway:</p>
                            <textarea
                                value={rerollReason}
                                onChange={(e) => setRerollReason(e.target.value)}
                                placeholder="e.g., Winner did not respond, Wrong person selected, etc."
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    marginBottom: '1rem'
                                }}
                            />
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={handleReroll}
                                    disabled={rerolling}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        background: 'linear-gradient(135deg, #9147ff 0%, #6441a5 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {rerolling ? 'Rerolling...' : '✓ Confirm Reroll'}
                                </button>
                                <button
                                    onClick={() => setShowRerollModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        background: 'rgba(239, 68, 68, 0.2)',
                                        color: '#ef4444',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ✕ Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* History Section */}
                {isAuthenticated && (
                    <div className="twitch-history-section">
                        <button
                            onClick={() => showHistory ? setShowHistory(false) : loadGiveawayHistory()}
                            disabled={loadingHistory}
                            className="twitch-history-toggle"
                        >
                            <span className="toggle-icon">📜</span>
                            <span className="toggle-text">
                                {loadingHistory ? 'Loading History...' : showHistory ? 'Hide My History' : 'View My History'}
                            </span>
                            {!loadingHistory && giveawayHistory.length > 0 && (
                                <span className="history-count-badge">{giveawayHistory.length}</span>
                            )}
                        </button>

                        {showHistory && giveawayHistory.length > 0 && (
                            <div className="twitch-history-grid">
                                {giveawayHistory.map((giveaway) => (
                                    <div key={giveaway.verificationId} className="twitch-history-card">
                                        <div className="history-card-header">
                                            <div className="channel-info">
                                                <span className="channel-icon">📺</span>
                                                <h4>{giveaway.channelName}</h4>
                                            </div>
                                            <div className="verification-badge">
                                                #{giveaway.verificationId}
                                            </div>
                                        </div>
                                        
                                        <div className="history-card-content">
                                            <div className="history-stat-row">
                                                <div className="history-stat">
                                                    <span className="stat-icon">🎯</span>
                                                    <div className="stat-text">
                                                        <span className="stat-label">Keyword</span>
                                                        <span className="stat-value">{giveaway.keyword}</span>
                                                    </div>
                                                </div>
                                                <div className="history-stat">
                                                    <span className="stat-icon">👥</span>
                                                    <div className="stat-text">
                                                        <span className="stat-label">Participants</span>
                                                        <span className="stat-value">{giveaway.uniqueParticipants}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="history-stat-row">
                                                <div className="history-stat">
                                                    <span className="stat-icon">🏆</span>
                                                    <div className="stat-text">
                                                        <span className="stat-label">Winners</span>
                                                        <span className="stat-value">{giveaway.numberOfWinners}</span>
                                                    </div>
                                                </div>
                                                <div className="history-stat">
                                                    <span className="stat-icon">📅</span>
                                                    <div className="stat-text">
                                                        <span className="stat-label">Date</span>
                                                        <span className="stat-value">{new Date(giveaway.rolledAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {giveaway.rerollReason && (
                                                <div className="reroll-notice">
                                                    <span className="reroll-icon">🔄</span>
                                                    <span className="reroll-text">{giveaway.rerollReason}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Recent Giveaways */}
                <div className="twitch-history-section">
                    <button
                        onClick={() => showRecentGiveaways ? setShowRecentGiveaways(false) : loadRecentGiveaways()}
                        disabled={loadingRecent}
                        className="twitch-history-toggle"
                    >
                        <span className="toggle-icon">🌟</span>
                        <span className="toggle-text">
                            {loadingRecent ? 'Loading...' : showRecentGiveaways ? 'Hide Recent Giveaways' : 'View Recent Giveaways'}
                        </span>
                        {!loadingRecent && recentGiveaways.length > 0 && (
                            <span className="history-count-badge">{recentGiveaways.length}</span>
                        )}
                    </button>

                    {showRecentGiveaways && recentGiveaways.length > 0 && (
                        <div className="twitch-history-grid">
                            {recentGiveaways.map((giveaway) => (
                                <div key={giveaway.verificationId} className="twitch-history-card">
                                    <div className="history-card-header">
                                        <div className="channel-info">
                                            <span className="channel-icon">📺</span>
                                            <h4>{giveaway.channelName}</h4>
                                        </div>
                                        <div className="verification-badge">
                                            #{giveaway.verificationId}
                                        </div>
                                    </div>
                                    
                                    <div className="history-card-content">
                                        <div className="history-stat-row">
                                            <div className="history-stat">
                                                <span className="stat-icon">🎯</span>
                                                <div className="stat-text">
                                                    <span className="stat-label">Keyword</span>
                                                    <span className="stat-value">{giveaway.keyword}</span>
                                                </div>
                                            </div>
                                            <div className="history-stat">
                                                <span className="stat-icon">👥</span>
                                                <div className="stat-text">
                                                    <span className="stat-label">Participants</span>
                                                    <span className="stat-value">{giveaway.uniqueParticipants}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="history-stat-row">
                                            <div className="history-stat">
                                                <span className="stat-icon">🏆</span>
                                                <div className="stat-text">
                                                    <span className="stat-label">Winners</span>
                                                    <span className="stat-value">{giveaway.numberOfWinners}</span>
                                                </div>
                                            </div>
                                            <div className="history-stat">
                                                <span className="stat-icon">👤</span>
                                                <div className="stat-text">
                                                    <span className="stat-label">Rolled by</span>
                                                    <span className="stat-value">@{giveaway.rolledBy}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="history-date-footer">
                                            <span className="date-icon">📅</span>
                                            <span className="date-text">{new Date(giveaway.rolledAt).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="info-section">
                <h2>ℹ️ How to Use Twitch Chat Picker</h2>
                <div className="info-grid">
                    <div className="info-card">
                        <div className="info-icon">🎯</div>
                        <h3>Collect Entries</h3>
                        <p>
                            Manually add viewers who type your keyword (like !join) in chat. 
                            Copy their username and message into the entry form above.
                        </p>
                    </div>
                    <div className="info-card">
                        <div className="info-icon">🎲</div>
                        <h3>Fair Selection</h3>
                        <p>
                            Our system uses cryptographically secure randomization to ensure 
                            every participant has an equal chance to win.
                        </p>
                    </div>
                    <div className="info-card">
                        <div className="info-icon">🛡️</div>
                        <h3>Verification</h3>
                        <p>
                            Every giveaway gets a unique verification ID. Share it with your 
                            community to prove the roll was fair and transparent.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwitchPicker;
